import { Controller, useForm } from 'react-hook-form';
import { css } from '@emotion/react';
import type { Dayjs } from 'dayjs';
import { TaskOriginType, TaskStateType } from '@definitions';
import { CommonFilters, StateCheckbox } from '@components/inputs';
import { useAppTranslation } from '@hooks';
import { TxKeyPath } from '@i18n';
import { GetTasksPayloadType } from '@api';
import {
  Button,
  CheckboxGroup,
  Form,
  FormItem,
  RangePicker,
} from 'common-components';
import { FiltersDrawer, FiltersDrawerFooter } from '@components/display';

type TasksFiltersType = GetTasksPayloadType['filters'];

type FiltersTasksFormType = TasksFiltersType & {
  dueDates?: [Dayjs | null, Dayjs | null] | null;
};

type FiltersTasksPropsType = {
  visible: boolean;
  onFiltersChange?: (filters: TasksFiltersType) => void;
  onClose?: () => void;
};

const FiltersTasks = ({ visible, onClose, onFiltersChange }: FiltersTasksPropsType) => {
  const { control, handleSubmit, reset } = useForm<FiltersTasksFormType>({
    defaultValues: { model: {} },
  });
  const { translate } = useAppTranslation();

  const originOptions = Object.keys(TaskOriginType).map((value) => ({
    value: Number(value),
    label: translate(`common.states.taskOrigin.${value}` as TxKeyPath),
  }));

  const onSubmit = ({ dueDates, ...filters }: FiltersTasksFormType) => {
    if (onFiltersChange) {
      onFiltersChange({
        ...filters,
        model: {
          ...filters.model,
          dueDateMin: dueDates?.[0]?.startOf('day').toISOString(),
          dueDateMax: dueDates?.[1]?.endOf('day').toISOString(),
        },
      });
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <FiltersDrawer open={visible} onClose={onClose}>
      <Form
        onReset={() => {
          reset({ model: {} });
          onSubmit({ model: {} });
        }}
        onFinish={handleSubmit(onSubmit)}
        css={$form}
      >
        <CommonFilters<FiltersTasksFormType> control={control} />

        <Controller
          name={'model.state'}
          control={control}
          render={({ field }) => (
            <FormItem label={translate('project.taskline.table.state')}>
              <StateCheckbox
                values={TaskStateType}
                labelTxKey={'taskState'}
                preset={'task-state'}
                inputProps={{
                  onChange: field.onChange,
                  value: field.value,
                }}
              />
            </FormItem>
          )}
        />

        <Controller
          name={'model.origin'}
          control={control}
          render={({ field }) => (
            <FormItem label={translate('project.taskline.table.origin')}>
              <CheckboxGroup
                options={originOptions}
                onChange={field.onChange}
                value={field.value}
              />
            </FormItem>
          )}
        />

        <Controller
          name={'dueDates'}
          control={control}
          render={({ field }) => (
            <FormItem label={translate('project.taskline.table.deadline')}>
              <RangePicker
                allowEmpty={[true, true]}
                onChange={field.onChange}
                value={field.value}
              />
            </FormItem>
          )}
        />

        <FiltersDrawerFooter>
          <Button htmlType="reset">{translate('common.buttons.cancel')}</Button>
          <Button type="primary" htmlType="submit">
            {translate('common.buttons.apply')}
          </Button>
        </FiltersDrawerFooter>
      </Form>
    </FiltersDrawer>
  );
};

const $form = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export default FiltersTasks;
